import React, { useState, useEffect } from 'react';
import {
    Grid,
    TextField,
    Typography,
    Container,
    IconButton,
    Box,
    InputAdornment,
    Alert,
    Fade,
    CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff, Email, Lock } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { loginSuccess } from '../../redux/actions/authAction';
import { fetchSubscription } from '../../redux/actions/subscriptionAction';
import { fetchPricing } from '../../redux/actions/pricingActions';
import { backendClient } from '../../api/backend';
import CustomButton from '../../components/CustomButton';
import LanguageSelector from '../../components/LanguageSelector';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { isMobile } from '../../utils/utils';

const GOLD = '#c9a96e';

const fadeInUpKeyframes = {
    '@keyframes fadeInUp': {
        from: { opacity: 0, transform: 'translateY(24px)' },
        to: { opacity: 1, transform: 'translateY(0)' },
    },
};

const stagger = (index) => ({
    opacity: 0,
    animation: 'fadeInUp 0.7s cubic-bezier(0.16, 1, 0.3, 1) forwards',
    animationDelay: `${index * 0.08}s`,
});

const inputSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        backgroundColor: 'rgba(255, 255, 255, 0.03)',
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
        color: '#fff',
        transition: 'all 0.2s ease',
        '& fieldset': {
            borderColor: 'rgba(255, 255, 255, 0.08)',
            transition: 'border-color 0.2s ease',
        },
        '&:hover fieldset': {
            borderColor: 'rgba(255, 255, 255, 0.16)',
        },
        '&.Mui-focused fieldset': {
            borderColor: GOLD,
            borderWidth: '1.5px',
        },
        '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
            WebkitBoxShadow: '0 0 0 1000px #111111 inset',
            WebkitTextFillColor: '#fff',
            caretColor: '#fff',
            borderRadius: '12px',
            transition: 'background-color 5000s ease-in-out 0s',
        },
    },
    '& .MuiInputLabel-root': {
        fontFamily: '"DM Sans", sans-serif',
        color: 'rgba(255, 255, 255, 0.35)',
        fontSize: '14px',
        '&.Mui-focused': {
            color: GOLD,
        },
    },
    '& .MuiFormHelperText-root': {
        fontFamily: '"DM Sans", sans-serif',
    },
    '& .MuiInputAdornment-root .MuiSvgIcon-root': {
        color: 'rgba(255, 255, 255, 0.25)',
        fontSize: '20px',
    },
};

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

const InvitationScreen = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const query = useQuery();
    const token = query.get('token');
    const isMobileView = isMobile();

    const [tokenInfo, setTokenInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [passwordError, setPasswordError] = useState(false);
    const [confirmPasswordError, setConfirmPasswordError] = useState(false);
    const [registering, setRegistering] = useState(false);

    // For non-invite-org flow (existing team invite)
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');

    const isInviteOrg = !!tokenInfo?.invite_org;

    useEffect(() => {
        const verifyToken = async () => {
            try {
                const base64Url = token.split('.')[1];
                const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                const jsonPayload = decodeURIComponent(
                    atob(base64)
                        .split('')
                        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                        .join('')
                );
                const payload = JSON.parse(jsonPayload);

                if (!payload.invitation || !payload.email) {
                    throw new Error('Invalid invitation token');
                }

                setTokenInfo(payload);
                setEmail(payload.email);

                // Set page language from token (admin chose fr/en when inviting)
                const lang = payload.lang;
                if (lang && ['fr', 'en', 'ar'].includes(lang) && i18n.language !== lang) {
                    i18n.changeLanguage(lang);
                }
            } catch (err) {
                console.error('Error verifying invitation token:', err);
                setError(t('invalid_invitation_link', 'Invalid or expired invitation link'));
            } finally {
                setLoading(false);
            }
        };

        if (token) {
            verifyToken();
        } else {
            setError(t('missing_token', 'Invitation token is missing'));
            setLoading(false);
        }
    }, [token, t]);

    const handleSubmit = async () => {
        const isValidPassword = password.length >= 6 && /[!@#$%^&*(),.+?":{}|<>]/.test(password);
        const isPasswordMatch = password === confirmPassword;

        setPasswordError(!isValidPassword);
        setConfirmPasswordError(!isPasswordMatch);

        if (!isValidPassword || !isPasswordMatch) return;

        // For non-invite-org, validate name fields
        if (!isInviteOrg) {
            if (!firstName.trim() || !lastName.trim()) {
                setError(t('name_required', 'First name and last name are required'));
                return;
            }
        }

        setRegistering(true);
        setError('');

        try {
            const response = await backendClient.post('api/user/register-invited', {
                email,
                first_name: isInviteOrg ? '' : firstName,
                last_name: isInviteOrg ? '' : lastName,
                phone_number: isInviteOrg ? '' : phoneNumber,
                organization_name: tokenInfo?.organization_name,
                password,
                user_type: 'user',
                token,
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Error registering account');
            }

            const data = await response.json();

            if (data.token && data.user) {
                localStorage.setItem('access_token', data.token.access_token);
                localStorage.setItem('token_type', data.token.token_type || 'bearer');
                if (data.token.token_version) {
                    localStorage.setItem('token_version', data.token.token_version);
                }

                dispatch(loginSuccess(data));
                dispatch(fetchSubscription());
                dispatch(fetchPricing());

                // Language is already set in i18n from token decode,
                // and persisted in DB by the backend (UserAppPreference).
                // fetchAppPreferences will pick it up on next page load.

                navigate('/');
            } else {
                throw new Error('Invalid response from server');
            }
        } catch (err) {
            console.error('Error registering invited user:', err);
            if (err.message?.includes('already exists')) {
                setError(t('email_already_exists', 'An account with this email already exists.'));
            } else if (err.message?.includes('Invalid or expired invitation token')) {
                setError(t('expired_invitation', 'Your invitation link has expired. Please contact your administrator.'));
            } else {
                setError(err.message || t('registration_error', 'Error registering account'));
            }
        } finally {
            setRegistering(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') handleSubmit();
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#000' }}>
                <CircularProgress sx={{ color: GOLD }} />
            </Box>
        );
    }

    // Fatal error (bad token) — show standalone error
    if (error && !tokenInfo) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#000', px: 3 }}>
                <Box sx={{ textAlign: 'center', maxWidth: 420 }}>
                    <Alert severity="error" sx={{
                        mb: 3, borderRadius: '12px', fontFamily: '"DM Sans", sans-serif',
                        backgroundColor: 'rgba(239, 68, 68, 0.08)', color: '#fca5a5',
                        border: '1px solid rgba(239, 68, 68, 0.15)',
                        '& .MuiAlert-icon': { color: '#f87171' },
                    }}>
                        {error}
                    </Alert>
                    <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '14px', color: 'rgba(255,255,255,0.4)' }}>
                        {t('contact_admin', 'Please contact your administrator for assistance.')}
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                backgroundColor: '#000',
                display: 'flex',
                position: 'relative',
                overflow: 'hidden',
                ...fadeInUpKeyframes,
            }}
        >
            {/* Grain texture overlay */}
            <Box
                sx={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    opacity: 0.025, pointerEvents: 'none', zIndex: 1,
                    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
                    backgroundRepeat: 'repeat', backgroundSize: '256px 256px',
                }}
            />

            {/* Language selector */}
            <Box sx={{ position: 'absolute', top: 20, right: 24, zIndex: 10 }}>
                <LanguageSelector variant="dark" />
            </Box>

            <Grid container sx={{ minHeight: '100vh', position: 'relative', zIndex: 2 }}>
                {/* Left Column — Branding */}
                {!isMobileView && (
                    <Grid
                        item xs={12} md={5}
                        sx={{
                            background: '#000', display: 'flex', flexDirection: 'column',
                            justifyContent: 'center', alignItems: 'center', position: 'relative', padding: 6,
                        }}
                    >
                        {/* Radial gold glow */}
                        <Box sx={{
                            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                            background: 'radial-gradient(ellipse 70% 50% at 50% 40%, rgba(201, 169, 110, 0.05) 0%, transparent 70%)',
                            pointerEvents: 'none',
                        }} />

                        {/* Subtle grid overlay */}
                        <Box sx={{
                            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, opacity: 0.025, pointerEvents: 'none',
                            backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
                            backgroundSize: '80px 80px',
                        }} />

                        {/* Logo */}
                        <Box sx={{ ...stagger(0), mb: 5 }}>
                            <Box sx={{
                                width: 100, height: 100, borderRadius: '50%', display: 'flex',
                                justifyContent: 'center', alignItems: 'center',
                                border: '1px solid rgba(255,255,255,0.06)', backgroundColor: 'rgba(255,255,255,0.02)', overflow: 'hidden',
                            }}>
                                <img src="/logo/logo-circle-dark-mode.svg" alt="Lawis Logo"
                                    style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '10px' }} />
                            </Box>
                        </Box>

                        {/* Heading */}
                        <Typography sx={{
                            ...stagger(1), fontFamily: '"Cormorant Garamond", Georgia, serif',
                            fontWeight: 300, fontSize: 'clamp(2rem, 3.5vw, 3.2rem)', color: '#fff',
                            textAlign: 'center', letterSpacing: '-0.02em', lineHeight: 1.1, mb: 2.5, zIndex: 2, position: 'relative',
                        }}>
                            {isInviteOrg
                                ? t('auth_welcome_title', 'Welcome to Lawis')
                                : t('auth_join_title', 'Join Lawis')
                            }
                        </Typography>

                        {/* Subtitle */}
                        <Typography sx={{
                            ...stagger(2), fontFamily: '"DM Sans", sans-serif', fontWeight: 400,
                            fontSize: '15px', color: 'rgba(255, 255, 255, 0.40)', textAlign: 'center',
                            maxWidth: '380px', lineHeight: 1.7, zIndex: 2, position: 'relative',
                        }}>
                            {isInviteOrg
                                ? t('auth_invite_org_subtitle', 'Set up your password to access your legal workspace.')
                                : t('auth_join_subtitle', 'Create your account to access our secure legal platform.')
                            }
                        </Typography>

                        {/* Decorative gold line */}
                        <Box sx={{ ...stagger(3), width: '48px', height: '1.5px', backgroundColor: GOLD, opacity: 0.4, mt: 4, borderRadius: '1px' }} />
                    </Grid>
                )}

                {/* Right Column — Form */}
                <Grid
                    item xs={12} md={7}
                    sx={{
                        display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column',
                        backgroundColor: '#0d0d0d',
                        borderLeft: isMobileView ? 'none' : '1px solid rgba(255, 255, 255, 0.04)',
                        position: 'relative', overflowY: 'auto', maxHeight: '100vh', py: 4,
                    }}
                >
                    {/* Mobile logo */}
                    {isMobileView && (
                        <Box sx={{ ...stagger(0), mb: 3, mt: 2 }}>
                            <Box sx={{
                                width: 72, height: 72, borderRadius: '50%', display: 'flex',
                                justifyContent: 'center', alignItems: 'center',
                                border: '1px solid rgba(255,255,255,0.06)', backgroundColor: 'rgba(255,255,255,0.02)', overflow: 'hidden',
                            }}>
                                <img src="/logo/logo-circle-dark-mode.svg" alt="Lawis Logo"
                                    style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '8px' }} />
                            </Box>
                        </Box>
                    )}

                    <Container maxWidth="sm" sx={{ maxWidth: '520px !important', px: isMobileView ? 3 : 4 }}>
                        {/* Section label */}
                        <Typography sx={{
                            ...stagger(0), fontFamily: '"DM Sans", sans-serif', fontSize: '11px',
                            fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase',
                            color: 'rgba(255, 255, 255, 0.25)', mb: 2,
                        }}>
                            {isInviteOrg
                                ? t('auth_setup_label', 'ACCOUNT SETUP')
                                : t('auth_signup_title', 'SIGN UP')
                            }
                        </Typography>

                        {/* Main heading */}
                        <Typography sx={{
                            ...stagger(1), fontFamily: '"Cormorant Garamond", Georgia, serif',
                            fontWeight: 300, fontSize: isMobileView ? '1.8rem' : '2.4rem',
                            color: '#fff', letterSpacing: '-0.02em', lineHeight: 1.1, mb: 1,
                        }}>
                            {isInviteOrg
                                ? t('auth_setup_heading', 'Create your password')
                                : t('auth_signup_subtitle', 'Set up your account to get started')
                            }
                        </Typography>

                        {/* Decorative line */}
                        <Box sx={{ ...stagger(2), width: '32px', height: '1.5px', backgroundColor: GOLD, opacity: 0.5, mb: 3.5, mt: 2, borderRadius: '1px' }} />

                        {/* Org info badge for invite-org */}
                        {isInviteOrg && tokenInfo?.organization_name && (
                            <Box sx={{
                                ...stagger(2), display: 'flex', alignItems: 'center', gap: 1.5,
                                mb: 3, px: 2, py: 1.5, borderRadius: '10px',
                                backgroundColor: 'rgba(201,169,110,0.07)', border: '1px solid rgba(201,169,110,0.2)',
                            }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: GOLD, flexShrink: 0 }} />
                                <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.55)' }}>
                                    {t('setting_up_for', 'Setting up your account to try')}{' '}
                                    <Typography component="span" sx={{ color: GOLD, fontWeight: 700, fontFamily: 'inherit', fontSize: 'inherit' }}>
                                        Lawis
                                    </Typography>
                                </Typography>
                            </Box>
                        )}

                        {/* Error alert */}
                        {error && (
                            <Fade in={!!error}>
                                <Alert severity="error" sx={{
                                    mb: 3, borderRadius: '12px', fontFamily: '"DM Sans", sans-serif',
                                    backgroundColor: 'rgba(239, 68, 68, 0.08)', color: '#fca5a5',
                                    border: '1px solid rgba(239, 68, 68, 0.15)',
                                    '& .MuiAlert-icon': { color: '#f87171' },
                                }}>
                                    {error}
                                </Alert>
                            </Fade>
                        )}

                        {/* Form fields */}
                        <Box sx={stagger(3)}>
                            <Grid container spacing={2}>
                                {/* Name fields — only for non-invite-org (team invite) */}
                                {!isInviteOrg && (
                                    <>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth label={t('first_name', 'First Name')}
                                                value={firstName} onChange={e => setFirstName(e.target.value)}
                                                required onKeyPress={handleKeyPress} sx={inputSx}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth label={t('last_name', 'Last Name')}
                                                value={lastName} onChange={e => setLastName(e.target.value)}
                                                required onKeyPress={handleKeyPress} sx={inputSx}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth label={t('phone_number', 'Phone Number')}
                                                value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)}
                                                onKeyPress={handleKeyPress} sx={inputSx}
                                            />
                                        </Grid>
                                    </>
                                )}

                                {/* Email — disabled */}
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth label={t('auth_email_label', 'Email')}
                                        value={email} disabled
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start"><Email /></InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            ...inputSx,
                                            '& .MuiOutlinedInput-root.Mui-disabled': {
                                                backgroundColor: 'rgba(255, 255, 255, 0.02)',
                                                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.05)' },
                                            },
                                            '& .MuiInputLabel-root.Mui-disabled': { color: 'rgba(255, 255, 255, 0.25)' },
                                            '& .MuiOutlinedInput-input.Mui-disabled': { WebkitTextFillColor: 'rgba(255, 255, 255, 0.5)' },
                                        }}
                                    />
                                </Grid>

                                {/* Password */}
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth label={t('auth_password_label', 'Password')}
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={e => { setPassword(e.target.value); setPasswordError(false); }}
                                        error={passwordError}
                                        helperText={passwordError ? t('auth_password_requirements', 'Min. 6 characters with a special character') : ''}
                                        required onKeyPress={handleKeyPress}
                                        InputProps={{
                                            startAdornment: (<InputAdornment position="start"><Lock /></InputAdornment>),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" sx={{ color: 'rgba(255,255,255,0.25)' }}>
                                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={inputSx}
                                    />
                                </Grid>

                                {/* Confirm Password */}
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth label={t('auth_confirm_password', 'Confirm password')}
                                        type={showPassword ? 'text' : 'password'}
                                        value={confirmPassword}
                                        onChange={e => { setConfirmPassword(e.target.value); setConfirmPasswordError(false); }}
                                        error={confirmPasswordError}
                                        helperText={confirmPasswordError ? t('auth_passwords_not_match', 'Passwords do not match') : ''}
                                        required onKeyPress={handleKeyPress}
                                        InputProps={{
                                            startAdornment: (<InputAdornment position="start"><Lock /></InputAdornment>),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" sx={{ color: 'rgba(255,255,255,0.25)' }}>
                                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={inputSx}
                                    />
                                </Grid>

                                {/* Submit Button */}
                                <Grid item xs={12} sx={{ mt: 1 }}>
                                    <CustomButton
                                        text={isInviteOrg
                                            ? t('create_password', 'Create Password')
                                            : t('complete_registration', 'Complete Registration')
                                        }
                                        outlined={false}
                                        loading={registering}
                                        onClick={handleSubmit}
                                        disabled={registering || !password || password.length < 6 || password !== confirmPassword}
                                        sx={{
                                            width: '100%', py: 1.6, borderRadius: '12px',
                                            backgroundColor: '#fff', color: '#000',
                                            fontFamily: '"DM Sans", sans-serif', fontSize: '14px',
                                            fontWeight: 600, letterSpacing: '0.02em', textTransform: 'none',
                                            boxShadow: 'none', transition: 'all 0.2s ease',
                                            ':hover': { backgroundColor: 'rgba(255, 255, 255, 0.88)', boxShadow: 'none' },
                                            '&.Mui-disabled': { backgroundColor: 'rgba(255, 255, 255, 0.15)', color: 'rgba(255, 255, 255, 0.3)' },
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </Container>
                </Grid>
            </Grid>
        </Box>
    );
};

export default InvitationScreen;
